/**
 * CMMD /forge iframe chrome-less mode. The iframe URL carries only `?embed=1`.
 * Auth arrives via postMessage + `/api/auth/cmmd-embed/exchange`, never a token
 * query param.
 *
 * Client navigations often drop the query string (TanStack Router path changes
 * to `/new`, `/settings`, etc.). Persist the embed flag in sessionStorage on
 * first detection so chrome hide, switcher hide, and org lock keep working for
 * the life of the iframe tab.
 *
 * Product name for this flag: `cmmdEmbedded` (alias of `isCmmdEmbedMode`).
 * CMMD AppBar owns Focus / Help / Gear (settings). Forge sidebar keeps PR /
 * bug / plug / usage; avatar and Log out stay hidden when true.
 */

export const CMMD_EMBED_QUERY_PARAM = "embed";
export const CMMD_EMBED_QUERY_VALUE = "1";
export const CMMD_EMBED_MODE_SESSION_KEY = "cmmd-forge-embed-mode";

export function persistCmmdEmbedMode(): void {
  try {
    sessionStorage.setItem(CMMD_EMBED_MODE_SESSION_KEY, "1");
  } catch {
    // Private mode may block sessionStorage; URL detection still works on load.
  }
  if (typeof document !== "undefined") {
    document.documentElement.dataset.cmmdEmbed = "1";
  }
}

/** Test-only: drop the persisted embed flag. */
export function __clearCmmdEmbedModeForTests(): void {
  try {
    sessionStorage.removeItem(CMMD_EMBED_MODE_SESSION_KEY);
  } catch {
    // ignore
  }
  if (typeof document !== "undefined") {
    delete document.documentElement.dataset.cmmdEmbed;
  }
}

export function isCmmdEmbedMode(
  search: string = typeof window !== "undefined" ? window.location.search : "",
): boolean {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  if (params.get(CMMD_EMBED_QUERY_PARAM) === CMMD_EMBED_QUERY_VALUE) {
    if (typeof window !== "undefined") {
      persistCmmdEmbedMode();
    }
    return true;
  }
  if (typeof window === "undefined") return false;
  try {
    if (sessionStorage.getItem(CMMD_EMBED_MODE_SESSION_KEY) === "1") {
      document.documentElement.dataset.cmmdEmbed = "1";
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * Stable product flag: Forge is running inside CMMD `/forge`. Prefer this name
 * in UI hide/show decisions. Same detection as `isCmmdEmbedMode`.
 */
export function cmmdEmbedded(
  search: string = typeof window !== "undefined" ? window.location.search : "",
): boolean {
  return isCmmdEmbedMode(search);
}

export function withCmmdEmbedQuery(url: string): string {
  const parsed = new URL(
    url,
    typeof window !== "undefined" ? window.location.origin : "http://localhost",
  );
  parsed.searchParams.set(CMMD_EMBED_QUERY_PARAM, CMMD_EMBED_QUERY_VALUE);
  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}
